import { Button, Group, SegmentedControl, Select, Slider, Text, TextInput, Title, Tooltip } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { TbChevronLeft, TbChevronRight, TbPlayerPause, TbPlayerPlay } from 'react-icons/tb'

import TooltipIconButton from './TooltipIconButton'

export const TIME_SEARCH_ID = 'documentation-time-search'

const DocumentationToolbar = ({
  cursor,
  length,
  tracks,
  track,
  onTrack,
  query,
  onQuery,
  onJump,
  hint,
  hintMiss,
  playing,
  onTogglePlay,
  follow,
  onToggleFollow,
  satellite,
  onSatellite,
  onSelect,
  onLoadArchive,
}: {
  cursor: number
  length: number
  tracks: string[]
  track: string
  onTrack: (track: string) => void
  query: string
  onQuery: (query: string) => void
  onJump: () => void
  hint: string
  hintMiss: boolean
  playing: boolean
  onTogglePlay: () => void
  follow: boolean
  onToggleFollow: () => void
  satellite: boolean
  onSatellite: (satellite: boolean) => void
  onSelect: (position: number) => void
  onLoadArchive: () => void
}) => {
  const { t } = useTranslation('documentation')
  const playLabel = playing ? t('pause') : t('play')

  return (
    <Group
      gap="md"
      px="lg"
      py="md"
      wrap="wrap"
      style={{ borderBottom: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))' }}
    >
      <Title order={4}>{t('title')}</Title>
      <Button size="sm" variant="default" onClick={onLoadArchive}>
        {t('loadArchive')}
      </Button>
      <TextInput
        id={TIME_SEARCH_ID}
        size="sm"
        w={180}
        placeholder={t('timePlaceholder')}
        value={query}
        disabled={!length}
        onChange={event => onQuery(event.currentTarget.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') onJump()
        }}
      />
      <Text size="sm" c={hintMiss ? 'red' : 'dimmed'} miw={96}>
        {hint}
      </Text>
      {tracks.length > 1 ? (
        <Select
          size="sm"
          w={200}
          value={track || 'all'}
          data={[
            { value: 'all', label: t('allDrives', { count: tracks.length }) },
            ...tracks.map(id => ({ value: id, label: t('drive', { id }) })),
          ]}
          onChange={value => onTrack(!value || value === 'all' ? '' : value)}
        />
      ) : null}
      <TooltipIconButton label={t('previous')} disabled={cursor <= 0} onClick={() => onSelect(cursor - 1)}>
        <TbChevronLeft size={18} />
      </TooltipIconButton>
      <TooltipIconButton label={t('next')} disabled={cursor >= length - 1} onClick={() => onSelect(cursor + 1)}>
        <TbChevronRight size={18} />
      </TooltipIconButton>
      <TooltipIconButton
        label={playLabel}
        variant={playing ? 'filled' : 'default'}
        disabled={!length}
        onClick={onTogglePlay}
      >
        {playing ? <TbPlayerPause size={18} /> : <TbPlayerPlay size={18} />}
      </TooltipIconButton>
      <Slider
        style={{
          flex: '1 1 180px',
          '--slider-track-bg': 'light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-4))',
        }}
        min={0}
        max={Math.max(0, length - 1)}
        value={Math.max(0, cursor)}
        disabled={!length}
        label={null}
        onChange={onSelect}
      />
      <Text size="sm" c="dimmed" ff="monospace">
        {length ? cursor + 1 : 0} / {length}
      </Text>
      <Tooltip label={t('follow')} withArrow>
        <Button size="sm" variant={follow ? 'filled' : 'default'} onClick={onToggleFollow}>
          {t('follow')}
        </Button>
      </Tooltip>
      <SegmentedControl
        size="sm"
        value={satellite ? 'satellite' : 'map'}
        onChange={value => onSatellite(value === 'satellite')}
        data={[
          { value: 'map', label: t('map') },
          { value: 'satellite', label: t('satellite') },
        ]}
      />
    </Group>
  )
}

export default DocumentationToolbar
